import React, { useEffect, useState } from 'react';
import { KeyRound, LockKeyhole, Save, ShieldCheck, UserRound } from 'lucide-react';

const AdminSettingsSection = ({ adminUsername = '', onSaveCredentials, isSaving = false, infoMessage = '' }) => {
  const [username, setUsername] = useState(adminUsername);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    setUsername(adminUsername || '');
  }, [adminUsername]);

  const handleSubmit = async () => {
    setLocalError('');

    if (!currentPassword.trim()) {
      setLocalError('Current password is required.');
      return;
    }

    const trimmedUsername = username.trim();
    const trimmedNewPassword = newPassword.trim();

    if (!trimmedUsername && !trimmedNewPassword) {
      setLocalError('Enter a new username or a new password to update settings.');
      return;
    }

    if (trimmedNewPassword && trimmedNewPassword !== confirmPassword.trim()) {
      setLocalError('New password and confirm password must match.');
      return;
    }

    await onSaveCredentials({
      currentPassword: currentPassword.trim(),
      username: trimmedUsername || undefined,
      newPassword: trimmedNewPassword || undefined,
      resetForm: () => {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      },
    });
  };

  return (
    <div className="glass-panel rounded-[18px] p-4 space-y-4">
      <div>
        <div className="flex items-center gap-2 text-[#666666] text-sm font-['DM_Sans']">
          <ShieldCheck className="w-4 h-4 text-[#9333EA]" />
          Admin Settings
        </div>
        <p className="mt-1 text-xs text-[#777777] font-['DM_Sans']">
          Change your admin username or password. Current password is always required.
        </p>
      </div>

      <div>
        <label className="block text-sm text-[#666666] mb-1 font-['DM_Sans']">Username</label>
        <div className="relative">
          <UserRound className="w-4 h-4 text-[#9333EA] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="w-full p-3 pl-10 rounded-[14px] border border-[#e9e9e9] bg-white"
            placeholder="Admin username"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm text-[#666666] mb-1 font-['DM_Sans']">Current Password</label>
        <div className="relative">
          <KeyRound className="w-4 h-4 text-[#9333EA] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            className="w-full p-3 pl-10 rounded-[14px] border border-[#e9e9e9] bg-white"
            placeholder="Current password"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm text-[#666666] mb-1 font-['DM_Sans']">New Password</label>
        <div className="relative">
          <LockKeyhole className="w-4 h-4 text-[#9333EA] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full p-3 pl-10 rounded-[14px] border border-[#e9e9e9] bg-white"
            placeholder="Leave blank to keep current password"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm text-[#666666] mb-1 font-['DM_Sans']">Confirm New Password</label>
        <input
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className="w-full p-3 rounded-[14px] border border-[#e9e9e9] bg-white"
          placeholder="Confirm new password"
        />
      </div>

      {(localError || infoMessage) && (
        <p className={`text-xs font-['DM_Sans'] rounded-[12px] p-3 ${localError ? 'bg-red-50 text-red-600' : 'bg-white/70 text-[#333333]'}`}>
          {localError || infoMessage}
        </p>
      )}

      <button
        onClick={handleSubmit}
        disabled={isSaving}
        className="w-full py-3 rounded-full bg-gradient-to-r from-[#9333EA] to-[#EC4899] text-white font-semibold disabled:opacity-60 flex items-center justify-center gap-2"
      >
        <Save className="w-4 h-4" />
        {isSaving ? 'Saving...' : 'Update Admin Credentials'}
      </button>
    </div>
  );
};

export default AdminSettingsSection;
